// src/components/Home/ProjectModal.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Fade,
  Backdrop,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// Styled Modal Box
const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '1200px',
  maxHeight: '90vh',
  backgroundColor: '#FFFFFF',
  borderRadius: 16,
  boxShadow: '0px 20px 60px rgba(0,0,0,0.3)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('md')]: {
    width: '95%',
    maxHeight: '95vh',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    borderRadius: 0,
  },
}));

const ModalHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderBottom: '1px solid #E0E0E0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: '#FAFAFA',
}));

const ModalContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const GallerySection = styled(Box)(({ theme }) => ({
  flex: '1.2',
  backgroundColor: '#1A1A1A',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    minHeight: '400px',
  },
}));

const MainImageContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
}));

const MainImage = styled('img')(() => ({
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
  userSelect: 'none',
}));

const ThumbnailContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  backgroundColor: '#0D0D0D',
  overflowX: 'auto',
  '&::-webkit-scrollbar': { height: 6 },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#5A5A5A',
    borderRadius: 3,
  },
}));

const Thumbnail = styled('img')(({ active }) => ({
  width: 80,
  height: 60,
  objectFit: 'cover',
  borderRadius: 4,
  cursor: 'pointer',
  opacity: active ? 1 : 0.6,
  border: active ? '2px solid #D2691E' : '2px solid transparent',
  transition: 'all 0.3s ease',
  '&:hover': { opacity: 1, transform: 'scale(1.05)' },
}));

const DetailsSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(4),
  overflowY: 'auto',
  backgroundColor: '#FFFFFF',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const NavButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  color: '#2C2416',
  zIndex: 1,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
  },
  '&.prev': { left: theme.spacing(2) },
  '&.next': { right: theme.spacing(2) },
}));

const CTAButton = styled(Button)(() => ({
  backgroundColor: '#D2691E',
  color: '#FFFFFF',
  padding: '14px 32px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#E08D4C',
  },
}));

const ProjectModal = ({ open, handleClose, project }) => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!project) return null;

  const projectImages =
    project.images && project.images.length > 0
      ? project.images
      : [
          project.mainImage || `https://picsum.photos/seed/${project._id}/800/600`,
          `https://picsum.photos/seed/${project._id}1/800/600`,
          `https://picsum.photos/seed/${project._id}2/800/600`,
          `https://picsum.photos/seed/${project._id}3/800/600`,
        ];

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? projectImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === projectImages.length - 1 ? 0 : prev + 1
    );
  };

  const handleThumbnailClick = (index) => setCurrentImageIndex(index);

  const handleModalClose = () => {
    setCurrentImageIndex(0);
    handleClose();
  };

  // ✅ Updated navigation: goes to Contact Us page
  const goToContactPage = () => {
    handleModalClose();
    navigate("/contact");
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
      }}
    >
      <Fade in={open}>
        <ModalBox>
          {/* Header */}
          <ModalHeader>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#2C2416' }}>
              {project.title}
            </Typography>
            <IconButton onClick={handleModalClose} size="large">
              <CloseIcon />
            </IconButton>
          </ModalHeader>

          {/* Content */}
          <ModalContent>
            {/* Image Gallery */}
            <GallerySection>
              <MainImageContainer>
                <NavButton className="prev" onClick={handlePrevImage}>
                  <ArrowBackIosIcon />
                </NavButton>
                <MainImage
                  src={projectImages[currentImageIndex]}
                  alt={`${project.title} - Image ${currentImageIndex + 1}`}
                />
                <NavButton className="next" onClick={handleNextImage}>
                  <ArrowForwardIosIcon />
                </NavButton>
              </MainImageContainer>

              <ThumbnailContainer>
                {projectImages.map((image, index) => (
                  <Thumbnail
                    key={index}
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    active={index === currentImageIndex}
                    onClick={() => handleThumbnailClick(index)}
                  />
                ))}
              </ThumbnailContainer>
            </GallerySection>

            {/* Details Section */}
            <DetailsSection>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#2C2416', mb: 2 }}>
                Project Overview
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: '#5C4033', lineHeight: 1.8, mb: 4 }}
              >
                {project.fullDescription ||
                  project.shortDescription ||
                  `This ${project.title?.toLowerCase() || 'project'} showcases our commitment to quality craftsmanship and attention to detail.`}
              </Typography>

              {/* CTA */}
              <Box sx={{ mt: 'auto', pt: 3 }}>
                <CTAButton variant="contained" fullWidth onClick={goToContactPage}>
                  Get a Free Quote
                </CTAButton>
              </Box>
            </DetailsSection>
          </ModalContent>
        </ModalBox>
      </Fade>
    </Modal>
  );
};

export default ProjectModal;
